import type { GinStepApplicationItem } from '@hellodarwin/core/lib/features/entities/gin-step-entities';
import type ClientApiV2 from './client-api-v2';
import type { GinStepDocumentRequest } from './client-api';

export interface ProgramDocumentRequirement {
  id: string;
  program_id: string;
  form_application_id: string;
  item_id: string;
  document_name: string;
  kind: 'file' | 'folder';
  asset_type?: string;
  custom_asset_type?: string;
  fulfilled: boolean;
  application_item?: GinStepApplicationItem;
}

export interface ProgramWorkingDocument {
  id: string;
  program_id: string;
  title: string;
  url: string;
  file_id: string;
  document_type:
    | 'google_docs'
    | 'google_sheets'
    | 'google_slides'
    | 'pdf'
    | 'file';
}

export interface ProgramDocuments {
  program_id: string;
  required_documents: ProgramDocumentRequirement[];
  working_documents: ProgramWorkingDocument[];
  working_documents_available?: boolean;
}

export const getProgramDocuments = async (
  api: ClientApiV2,
  programId: string,
  locale: string,
): Promise<ProgramDocuments> => {
  const { data } = await api.get<ProgramDocuments>(
    `/programs/${encodeURIComponent(programId)}/documents`,
    { params: { locale } },
  );
  return {
    ...data,
    required_documents: data.required_documents ?? [],
    working_documents: data.working_documents ?? [],
    working_documents_available: data.working_documents_available !== false,
  };
};

export const saveProgramDocument = async (
  api: ClientApiV2,
  programId: string,
  item: GinStepApplicationItem,
  update: boolean,
) => {
  const path = `/programs/${encodeURIComponent(programId)}/documents/items`;
  const response = update
    ? await api.put<{ program_status_trigger?: string }>(path, item)
    : await api.post<{ program_status_trigger?: string }>(path, item);
  return response.data;
};

export const removeProgramDocument = async (
  api: ClientApiV2,
  requirement: ProgramDocumentRequirement,
  assetIds: string[] = [],
) => {
  const itemApplicationId = requirement.application_item?.item_application_id;
  if (!itemApplicationId) throw new Error('missing document submission');
  await api.delete(
    `/programs/${encodeURIComponent(requirement.program_id)}/documents/items`,
    {
      data: {
        form_application_id: requirement.form_application_id,
        item_id: requirement.item_id,
        item_application_id: itemApplicationId,
        asset_ids: assetIds,
      },
    },
  );
};

// The dashboard/Documents keep the existing personally assigned pending feed.
// Hydrate the program before editing so a newly fulfilled slot cannot be replaced
// using stale pending-request data.
export const requirementFromPersonalRequest = (
  request: GinStepDocumentRequest,
): ProgramDocumentRequirement => ({
  id: `${request.form_application_id}:${request.item_id}`,
  program_id: request.program_id,
  form_application_id: request.form_application_id,
  item_id: request.item_id,
  document_name: request.document_name,
  kind: request.field_type === 'asset_bucket' ? 'folder' : 'file',
  asset_type: request.asset_type,
  custom_asset_type: request.custom_asset_type,
  fulfilled: false,
});
